import { Icons } from '@/assets/icons/icons';

import { AppSetupConfiguration } from './app-setup-configuration';
import { AppleCalendarConfiguration } from './apple-calendar-configuration';
import { AppleMailConfiguration } from './apple-mail-configuration';
import { AppleNotesConfiguration } from './apple-notes-configuration';
import { ChannelConfiguration } from './channel-configuration';
import { ComputerUseConfiguration } from './computer-use-configuration';
import { ContactsAccessConfiguration } from './contacts-configuration';
import { ReminderListsConfiguration } from './eventkit-configuration';
import {
  FilesystemFoldersConfiguration,
  ObsidianVaultsConfiguration,
} from './local-paths-configuration';
import { LocalServerConfiguration } from './local-server-configuration';
import { LogseqConfiguration } from './logseq-configuration';
import { meetingNoteSourceForProvider } from './meeting-note-source-for-provider';
import { MeetingNotesConfiguration } from './meeting-notes-configuration';
import { TerminalConfiguration } from './terminal-configuration';
import { XeroConfiguration } from './xero-configuration';

import type {
  ConfigurationContext,
  DesktopConfigurationDecision,
  DesktopConfigurationTab,
} from './configuration.types';
import type {
  IntegrationProvider,
  IntegrationSettings,
} from '@littlebird/integrations';

const LOCAL_SERVER_TAB: DesktopConfigurationTab = {
  id: 'configuration:local-server',
  label: 'Settings',
  icon: Icons.cog,
  Component: LocalServerConfiguration,
};

const MAIL_GENERAL_TAB: DesktopConfigurationTab = {
  id: 'configuration:mail-accounts',
  label: 'General',
  icon: Icons.mail,
  Component: AppleMailConfiguration,
};

const APP_SETUP_TAB: DesktopConfigurationTab = {
  id: 'configuration:app-setup',
  label: 'General',
  icon: Icons.cog,
  Component: AppSetupConfiguration,
};

const APPLE_NOTES_GENERAL_TAB: DesktopConfigurationTab = {
  id: 'configuration:apple-notes',
  label: 'General',
  icon: Icons.cog,
  Component: AppleNotesConfiguration,
};

const APPLE_CALENDAR_GENERAL_TAB: DesktopConfigurationTab = {
  id: 'configuration:apple-calendar',
  label: 'General',
  icon: Icons.calendar,
  Component: AppleCalendarConfiguration,
};

const COMPUTER_USE_GENERAL_TAB: DesktopConfigurationTab = {
  id: 'configuration:computer-use',
  label: 'General',
  icon: Icons.cog,
  Component: ComputerUseConfiguration,
};

const FOLDERS_TAB: DesktopConfigurationTab = {
  id: 'configuration:folders',
  label: 'Folders',
  icon: Icons.folder,
  Component: FilesystemFoldersConfiguration,
};

const VAULTS_TAB: DesktopConfigurationTab = {
  id: 'configuration:vaults',
  label: 'Vaults',
  icon: Icons.book,
  Component: ObsidianVaultsConfiguration,
};

const LOGSEQ_GENERAL_TAB: DesktopConfigurationTab = {
  id: 'configuration:logseq',
  label: 'General',
  icon: Icons.cog,
  Component: LogseqConfiguration,
};

const XERO_GENERAL_TAB: DesktopConfigurationTab = {
  id: 'configuration:xero',
  label: 'General',
  icon: Icons.cog,
  Component: XeroConfiguration,
};

const LISTS_TAB: DesktopConfigurationTab = {
  id: 'configuration:lists',
  label: 'General',
  icon: Icons.todoList,
  Component: ReminderListsConfiguration,
};

const ACCESS_TAB: DesktopConfigurationTab = {
  id: 'configuration:access',
  label: 'General',
  icon: Icons.user,
  Component: ContactsAccessConfiguration,
};

const TERMINAL_TAB: DesktopConfigurationTab = {
  id: 'configuration:terminal',
  label: 'General',
  icon: Icons.terminal,
  Component: TerminalConfiguration,
};

const CHANNEL_SETTINGS_TAB: DesktopConfigurationTab = {
  id: 'configuration:channel',
  label: 'Settings',
  icon: Icons.cog,
  Component: ChannelConfiguration,
};

const MEETING_NOTES_TAB: DesktopConfigurationTab = {
  id: 'configuration:meeting-notes',
  label: 'Meeting notes',
  icon: Icons.meetings,
  Component: MeetingNotesConfiguration,
};

const LOCAL_MCP_TABS_BY_TRUSTED_TEMPLATE: Readonly<
  Partial<Record<string, readonly DesktopConfigurationTab[]>>
> = {
  filesystem: [FOLDERS_TAB],
  obsidian: [VAULTS_TAB],
  logseq: [LOGSEQ_GENERAL_TAB],
  xero: [XERO_GENERAL_TAB],
  'apple-mail': [MAIL_GENERAL_TAB],
  'apple-notes': [APPLE_NOTES_GENERAL_TAB],
  things: [APP_SETUP_TAB],
  fantastical: [APP_SETUP_TAB],
  omnifocus: [APP_SETUP_TAB],
  imessage: [APP_SETUP_TAB],
  'macos-calendar': [APPLE_CALENDAR_GENERAL_TAB],
  'computer-use': [COMPUTER_USE_GENERAL_TAB],
};

const APPLE_NATIVE_TABS_BY_TOOLKIT: Record<
  Extract<IntegrationSettings, { kind: 'apple_native' }>['toolkit'],
  readonly DesktopConfigurationTab[]
> = {
  applereminders: [LISTS_TAB],
  applecontacts: [ACCESS_TAB],
  // Opt-in toggle; a working-directory/status tab is a follow-up.
  terminal: [TERMINAL_TAB],
};

const CHANNEL_TABS_BY_CHANNEL: Record<
  Extract<IntegrationSettings, { kind: 'channel' }>['channel'],
  readonly DesktopConfigurationTab[]
> = {
  imessage: [CHANNEL_SETTINGS_TAB],
  whatsapp_business: [CHANNEL_SETTINGS_TAB],
};

function resolveLocalMcpTabs(
  source: Extract<IntegrationSettings, { kind: 'local_mcp' }>
): DesktopConfigurationDecision {
  const tabs = source.trustedTemplateId
    ? LOCAL_MCP_TABS_BY_TRUSTED_TEMPLATE[source.trustedTemplateId]
    : undefined;
  return { kind: 'tabs', tabs: tabs ?? [LOCAL_SERVER_TAB] };
}

function resolveAppleTabs(
  toolkit: Extract<IntegrationSettings, { kind: 'apple_native' }>['toolkit']
): DesktopConfigurationDecision {
  return { kind: 'tabs', tabs: APPLE_NATIVE_TABS_BY_TOOLKIT[toolkit] };
}

function resolveChannelTabs(
  channel: Extract<IntegrationSettings, { kind: 'channel' }>['channel']
): DesktopConfigurationDecision {
  return { kind: 'tabs', tabs: CHANNEL_TABS_BY_CHANNEL[channel] };
}

export function resolveDesktopConfiguration(
  context: ConfigurationContext
): DesktopConfigurationDecision {
  const source = context.localSource;
  if (!source) return { kind: 'none', reason: 'remote_provider' };
  if (source.kind === 'local_mcp') return resolveLocalMcpTabs(source);
  if (source.kind === 'apple_native') return resolveAppleTabs(source.toolkit);
  return resolveChannelTabs(source.channel);
}

/** Tabs a remote provider gets from what Littlebird does with it, since it has no local settings */
export function resolveRemoteProviderTabs(
  provider: IntegrationProvider,
  { meetingNotesSyncEnabled }: { meetingNotesSyncEnabled: boolean }
): readonly DesktopConfigurationTab[] {
  return meetingNotesSyncEnabled && meetingNoteSourceForProvider(provider)
    ? [MEETING_NOTES_TAB]
    : [];
}
